import { useCallback } from 'react';
import {
  useSelectEntityItems,
  UseSelectEntityItemsReturnValue
} from '../useSelectEntityItems/useSelectEntityItems';

/**
 * A variant for useSelectEntityItems which supports null values.
 */
export function useNullableSelectEntityItems<TEntity, TValue>({
  value,
  entities,
  nullValueText,
  entityNotFoundText,
  getDisplayTextOfEntity,
  getKeyOfEntity,
  getValueOfEntity,
  onValueChanged
}: UseNullableSelectEntityItemsOptions<
  TEntity,
  TValue
>): UseNullableSelectEntityItemsReturnValue<TEntity> {
  const getValueOfEntityOrNull = useCallback(
    (entity: TEntity | null) => {
      if (entity === null) {
        return null;
      }

      return getValueOfEntity(entity);
    },
    [getValueOfEntity]
  );

  const getDisplayTextOfEntityOrNull = useCallback(
    (entity: TEntity | null) => {
      if (entity === null) {
        return nullValueText;
      }

      return getDisplayTextOfEntity(entity);
    },
    [nullValueText, getDisplayTextOfEntity]
  );

  const getKeyOfEntityOrNull = useCallback(
    (entity: TEntity | null) => {
      if (entity === null) {
        return 'useNullableSelectEntityItems.nullValue';
      }

      return getKeyOfEntity(entity);
    },
    [getKeyOfEntity]
  );

  return useSelectEntityItems({
    value,
    entities,
    entityNotFoundText,
    onValueChanged,
    getValueOfEntity: getValueOfEntityOrNull,
    getKeyOfEntity: getKeyOfEntityOrNull,
    getDisplayTextOfEntity: getDisplayTextOfEntityOrNull
  });
}

export type UseNullableSelectEntityItemsOptions<TEntity, TValue> = {
  value: TValue | null;
  /**
   * the null value should only occur once
   */
  entities: Array<TEntity | null>;
  nullValueText: string;
  entityNotFoundText: string;
  onValueChanged: (value: TValue | null) => void;
  getDisplayTextOfEntity: (entity: TEntity) => string;
  getKeyOfEntity: (entity: TEntity) => string;
  getValueOfEntity: (entity: TEntity) => TValue;
};

export type UseNullableSelectEntityItemsReturnValue<TEntity> =
  UseSelectEntityItemsReturnValue<TEntity | null>;
